import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { apiUrl } from '../config';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield } from 'react-icons/fi';
import { sanitizeEmail, sanitizeText, validators } from '../utils/sanitize';
import { readApiJson } from '../utils/apiResponse';
import { loginWithGoogleCredential, shouldCompleteProfile } from '../utils/googleAuth';
import { needsSubscriptionPayment } from '../utils/subscriptionGate';
import { FormField, FormAlert, formInputClass } from '../components/form';
import AuthSplitShell from '../components/auth/AuthSplitShell';
import AuthGoogleButton from '../components/auth/AuthGoogleButton';
import TransactionalEmailHint from '../components/auth/TransactionalEmailHint';
import { useGoogleClientId } from '../components/AsyncGoogleOAuthProvider';
import { googleSignInErrorMessage } from '../utils/googleOAuthOriginHelp';

const Login = () => {
  const { login } = useContext(AuthContext);
  const tc = useThemeClasses();
  const navigate = useNavigate();
  const googleClientId = useGoogleClientId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationInfo, setActivationInfo] = useState('');

  const inputWithIcon = (icon, inputProps, toggleProps) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{icon}</div>
      <input className={`${formInputClass(tc.inputClass)} pl-9 ${toggleProps ? 'pr-9' : ''}`} {...inputProps} />
      {toggleProps && (
        <button
          type="button"
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${tc.textMuted} hover:opacity-80`}
          {...toggleProps}
        />
      )}
    </div>
  );

  const postLoginPath = (user) => {
    if (shouldCompleteProfile(user)) return '/completar-perfil';
    if (needsSubscriptionPayment(user)) return '/planes?pago=pendiente';
    return '/inicio';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const sanitizedEmail = sanitizeEmail(email);

      const emailValidation = validators.email(sanitizedEmail);
      if (!emailValidation.valid) {
        setError(emailValidation.error);
        setLoading(false);
        return;
      }

      if (!password) {
        setError('La contraseña es requerida');
        setLoading(false);
        return;
      }

      const res = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: sanitizedEmail,
          password,
        }),
      });

      const contentType = res.headers.get('content-type') || '';
      let data;

      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        throw new Error('Respuesta no válida del servidor de autenticación.');
      }

      if (!res.ok) {
        const msg =
          data.code === 'USE_GOOGLE_SIGNIN'
            ? data.message
            : data.message || 'Email o contraseña incorrectos.';
        throw new Error(msg);
      }

      if (data.requiresTwoFactor) {
        setChallengeToken(data.challengeToken);
        setTwoFactorCode('');
        return;
      }

      login(data.user, data.token, data.refreshToken);
      navigate(postLoginPath(data.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendActivation = async () => {
    setError('');
    setActivationInfo('');
    const sanitizedEmail = sanitizeEmail(email);
    const emailValidation = validators.email(sanitizedEmail);
    if (!emailValidation.valid) {
      setError('Completá arriba el email con el que te registraste y pagaste.');
      return;
    }
    setActivationLoading(true);
    try {
      const res = await fetch(apiUrl('/api/signup/resend-activation'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sanitizedEmail }),
      });
      const data = await readApiJson(res);
      if (!res.ok) throw new Error(data.message || 'No pudimos enviar el email');
      setActivationInfo(data.message || 'Revisá tu bandeja de entrada.');
    } catch (err) {
      setError(err.message);
    } finally {
      setActivationLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const data = await loginWithGoogleCredential(credentialResponse.credential);
      if (data.requiresTwoFactor) {
        setChallengeToken(data.challengeToken);
        setTwoFactorCode('');
        return;
      }
      if (shouldCompleteProfile(data.user, { isNewUser: data.isNewUser, needsOnboarding: data.needsOnboarding })) {
        if (data.token && data.user) {
          login(data.user, data.token, data.refreshToken);
          navigate(postLoginPath(data.user), { replace: true });
          return;
        }
        navigate('/register', { replace: true, state: { googleAuth: data } });
        return;
      }
      login(data.user, data.token, data.refreshToken);
      navigate(postLoginPath(data.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/2fa/verify-login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken,
          code: twoFactorCode.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Código incorrecto');
      login(data.user, data.token, data.refreshToken);
      navigate(postLoginPath(data.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const dividerBg = tc.modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]';

  const loginFooter = !challengeToken ? (
    <button
      type="submit"
      form="login-form"
      disabled={loading}
      className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${tc.btnPrimary}`}
    >
      {loading ? (
        <>
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          Iniciando sesión...
        </>
      ) : (
        <>
          Entrar
          <FiArrowRight className="w-4 h-4" />
        </>
      )}
    </button>
  ) : null;

  return (
    <AuthSplitShell contentWidth="default" footer={loginFooter} centerVertically>
      <div className="mb-5 sm:mb-6">
        <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${tc.textPri}`}>
          {challengeToken ? 'Verificación en dos pasos' : 'Entrar'}
        </h2>
        <p className={`mt-2 text-sm ${tc.textMuted}`}>
          {challengeToken
            ? 'Ingresá el código de tu app autenticadora o uno de recuperación.'
            : 'Accedé a ventas, gastos, clientes y reportes desde un solo lugar.'}
        </p>
      </div>

      {challengeToken ? (
        <form onSubmit={handleTwoFactorSubmit} className="space-y-5">
          <FormField label="Código de verificación" required labelClass={tc.labelClass}>
            {inputWithIcon(<FiShield className={`h-4 w-4 ${tc.textMuted}`} />, {
              type: 'text',
              value: twoFactorCode,
              onChange: (event) => setTwoFactorCode(event.target.value),
              required: true,
              autoFocus: true,
              autoComplete: 'one-time-code',
              inputMode: 'text',
              placeholder: '123456 o XXXXX-XXXXX',
            })}
          </FormField>

          {error && (
            <FormAlert type="error" className="mb-0 whitespace-pre-line text-sm leading-snug">
              {error}
            </FormAlert>
          )}

          <button
            type="submit"
            disabled={loading || !twoFactorCode.trim()}
            className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${tc.btnPrimary}`}
          >
            {loading ? 'Verificando…' : 'Verificar y entrar'}
          </button>
          <button
            type="button"
            onClick={() => {
              setChallengeToken('');
              setTwoFactorCode('');
              setError('');
            }}
            className={`w-full text-sm font-medium ${tc.textMuted} hover:underline`}
          >
            Volver al inicio de sesión
          </button>
        </form>
      ) : (
      <form id="login-form" onSubmit={handleSubmit} className="w-full space-y-5">
        <AuthGoogleButton
          className="w-full"
          onSuccess={handleGoogleSuccess}
          onError={(detail) => setError(googleSignInErrorMessage({ clientId: googleClientId, ...detail }))}
          modoOscuro={tc.modoOscuro}
          text="signin_with"
        />

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <div className={`w-full border-t ${tc.divide}`} />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className={`px-3 ${dividerBg} ${tc.textMuted}`}>o con email</span>
          </div>
        </div>

        <FormField label="Correo electrónico" required labelClass={tc.labelClass}>
          {inputWithIcon(<FiMail className={`h-4 w-4 ${tc.textMuted}`} />, {
            type: 'email',
            value: email,
            onChange: (e) => setEmail(e.target.value),
            required: true,
            autoFocus: true,
            placeholder: 'nombre@empresa.com',
          })}
        </FormField>

        <FormField label="Contraseña" required labelClass={tc.labelClass}>
          {inputWithIcon(
            <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
            {
              type: showPassword ? 'text' : 'password',
              value: password,
              onChange: (e) => setPassword(e.target.value),
              required: true,
              placeholder: 'Tu contraseña',
            },
            {
              onClick: () => setShowPassword((v) => !v),
              tabIndex: -1,
              'aria-label': showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
              children: showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
            }
          )}
        </FormField>

        <div className="flex flex-col items-end gap-1 -mt-1 text-right">
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-[#1F80FF] hover:underline underline-offset-2"
          >
            ¿Olvidaste tu contraseña?
          </Link>
          <button
            type="button"
            onClick={handleResendActivation}
            disabled={activationLoading || loading}
            className="text-xs font-medium text-[#1F80FF] hover:underline underline-offset-2 disabled:opacity-50"
          >
            {activationLoading ? 'Enviando email…' : '¿Pagaste y no pudiste entrar? Reenviar activación'}
          </button>
        </div>

        {activationInfo && (
          <>
            <FormAlert type="success" className="mb-0">{sanitizeText(activationInfo)}</FormAlert>
            <TransactionalEmailHint />
          </>
        )}

        {error && (
          <FormAlert type="error" className="mb-0 whitespace-pre-line text-sm leading-snug">
            {error}
          </FormAlert>
        )}
      </form>
      )}

      {!challengeToken && (
        <p className={`mt-4 text-center text-sm ${tc.textSec}`}>
          ¿Todavía no tenés cuenta?{' '}
          <Link to="/register" className="font-semibold text-[#1F80FF] hover:underline">
            Crear cuenta gratis
          </Link>
        </p>
      )}
    </AuthSplitShell>
  );
};

export default Login;
